import React, { useState, useEffect } from 'react';
import { StarIcon } from '../../public/icons';
import { reviewService } from '../../services/reviewService';

/* ─────────────────────── Media Gallery ─────────────────────── */
const ReviewMedia = ({ media = [], reviewerName = 'Reviewer' }) => {
  if (!media.length) return null;

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {media.map((m, idx) => (
        <div
          key={m.publicId || idx}
          className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 border border-[#DBDBDB] overflow-hidden rounded"
        >
          {m.type === 'video' ? (
            <video
              src={m.url}
              className="w-full h-full object-cover"
              controls
              preload="metadata"
            />
          ) : (
            <a
              href={m.url}
              target="_blank"
              rel="noopener noreferrer"
              title={`View ${reviewerName}'s photo`}
              className="block w-full h-full"
            >
              <img
                src={m.url}
                alt={`Review media ${idx + 1}`}
                className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                loading="lazy"
              />
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────── Main Component ─────────────────────── */
const CustomerReviews = ({
  slug,
  onWriteReview,
  refreshKey = 0,
  limit = 10
}) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({
    averageRating: 0,
    totalReviews: 0,
    distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;

    let cancelled = false;

    const fetchReviews = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await reviewService.getProductReviews(slug, {
          page: 1,
          limit,
          sort: 'recent'
        });

        if (cancelled) return;

        if (data.success) {
          setReviews(data.reviews || []);
          setSummary(
            data.summary || {
              averageRating: 0,
              totalReviews: 0,
              distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
            }
          );
        } else {
          setError(data.message || 'Failed to load reviews');
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err.response?.data?.message || err.message || 'Failed to load reviews'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReviews();

    return () => {
      cancelled = true;
    };
  }, [slug, limit, refreshKey]);

  return (
    <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 pb-6 border-b-2 border-bordercolor">
        <h3 className="text-2xl sm:text-3xl font-medium text-[#1C2A46]">
          Customer Reviews
        </h3>
        {onWriteReview && (
          <button
            onClick={onWriteReview}
            className="text-[#1C2A46] text-sm sm:text-base font-normal px-4 sm:px-5 py-2.5 sm:py-3.5 hover:underline border border-bordercolor w-full sm:w-auto"
          >
            Write a review
          </button>
        )}
      </div>

      {/* Rating Summary */}
      <div className="bg-[#F5F5F5] p-4 sm:p-5 border border-[#E4E4E4]">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
          <div>
            <div className="text-3xl sm:text-4xl font-bold text-[#1C2A46] mb-1">
              {summary.averageRating ? summary.averageRating.toFixed(1) : '0.0'}
            </div>
            <div className="flex mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <StarIcon
                  key={star}
                  filled={star <= Math.round(summary.averageRating)}
                  className={`w-3 h-3 sm:w-4 sm:h-4 ${
                    star <= Math.round(summary.averageRating)
                      ? 'text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <div className="text-xs sm:text-sm text-[#6B635D]">
              {summary.totalReviews}{' '}
              review{summary.totalReviews === 1 ? '' : 's'}
            </div>
          </div>

          <div className="flex-1 w-full">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = summary.distribution?.[rating] || 0;
              const pct =
                summary.totalReviews > 0
                  ? (count / summary.totalReviews) * 100
                  : 0;
              return (
                <div key={rating} className="flex items-center mb-1">
                  <span className="w-5 sm:w-6 text-xs sm:text-sm text-[#6B635D]">
                    {rating}
                  </span>
                  <div className="flex-1 mx-2 sm:mx-3 bg-gray-200 rounded-full h-1.5 sm:h-2">
                    <div
                      className="bg-[#D2A146] h-1.5 sm:h-2 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-4 text-xs sm:text-sm text-[#6B635D] text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Individual Reviews */}
      <div className="space-y-4 text-left mt-5">
        {loading && (
          <p className="text-sm text-[#6B635D]">Loading reviews…</p>
        )}

        {!loading && error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        {!loading && !error && reviews.length === 0 && (
          <p className="text-sm text-[#6B635D]">
            No reviews yet. Be the first to write one!
          </p>
        )}

        {!loading &&
          !error &&
          reviews.map((review, idx) => (
            <div
              key={review._id}
              className={`pb-4 ${
                idx < reviews.length - 1 ? 'border-b border-[#F0F0F0]' : ''
              } ${idx > 0 ? 'pt-6 sm:pt-10' : ''}`}
            >
              <h5 className="font-medium text-[#1C2A46] mb-1">
                {review.reviewerName || 'Anonymous'}
              </h5>

              <div className="flex items-center mb-2 flex-wrap">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarIcon
                      key={star}
                      filled={star <= review.rating}
                      className={`w-3 h-3 sm:w-4 sm:h-4 ${
                        star <= review.rating
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-[#6B635D]">
                  {new Date(review.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
                {review.isVerifiedPurchase && (
                  <span className="ml-3 text-[10px] sm:text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                    Verified
                  </span>
                )}
              </div>

              {review.title && (
                <p className="font-medium text-[#1C2A46] text-sm sm:text-base mb-1">
                  {review.title}
                </p>
              )}

              <p className="text-xs sm:text-sm text-[#6B635D]">{review.text}</p>

              {/* 🟢 Media gallery — below the description */}
              <ReviewMedia
                media={review.media || []}
                reviewerName={review.reviewerName}
              />
            </div>
          ))}
      </div>
    </>
  );
};

export default CustomerReviews;